import React, { useLayoutEffect, useState } from "react";
import LayoutFooter from "../inc/LayoutFooter";
import LayoutHeader from "../inc/LayoutHeader";
import { useNavigate } from "react-router-dom";
import { RollbackOutlined } from "@ant-design/icons";
import {
  Layout,
  Col,
  Row,
  Select,
  DatePicker,
  Card,
  Form,
  Spin,
  Input,
  Button,
  Divider,
} from "antd";
import { displayMessage, getAPI, postAPI } from "../../utils/common";
import { ERROR_MSG_TYPE, SUCCESS_MESSAGE } from "../../constants/messages";
import { CREATE_LEAD, LOCATIONS, STAFF } from "../../constants/api";
import { HOTEL_TYPE } from "../../constants/constent";

function LeadAdd() {
  const { Content } = Layout;
  const { Option } = Select;
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [bdes, setBdes] = useState([]);
  const [country, setCountry] = useState([]);
  const [state, setState] = useState([]);
  const [city, setCity] = useState([]);

  const [countryID, setCountryID] = useState(102);
  const [stateID, setStateID] = useState(null);

  useLayoutEffect(() => {
    form.setFieldsValue({ country_id: countryID ?? 102 });
    fetchLocations(countryID ?? 102, stateID);
    fetchBDEs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch BDE users
  const fetchBDEs = () => {
    setLoading(true);
    getAPI(
      STAFF,
      (data) => {
        if (data.status) {
          const filteredBDEs = data.data.filter(
            (user) => user.user_type === "bde"
          );
          setBdes(filteredBDEs);
        } else setBdes([]);
        setLoading(false);
      },
      () => {
        setBdes([]);
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to fetch BDEs");
      }
    );
  };

  // Fetch country / state / city
  const fetchLocations = (countryID, stateID) => {
    setLoading(true);
    const url =
      LOCATIONS +
      (countryID ? countryID : "") +
      (stateID ? `&state_id=${stateID}` : "");
    getAPI(
      url,
      (data) => {
        setCountry(data.countries);
        setState(data.states);
        setCity(data.cities);
        setLoading(false);
      },
      () => {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to fetch locations");
      }
    );
  };

  const handleOnChange = (key, value) => {
    form.setFieldsValue({ [key]: value });
    if (key === "country_id") {
      setCountryID(value);
      setStateID(null);
      form.setFieldsValue({ state_id: null, city_id: null });
      fetchLocations(value, null);
    } else if (key === "state_id") {
      setStateID(value);
      form.setFieldsValue({ city_id: null });
      fetchLocations(countryID, value);
    }
  };

  const handleSubmit = (values) => {
    setLoading(true);
    const payload = { ...values };
    if (payload.next_followup_date) {
      payload.next_followup_date =
        payload.next_followup_date.format("YYYY-MM-DD");
    }
    postAPI(
      CREATE_LEAD,
      payload,
      (data) => {
        setLoading(false);
        if (data.status) {
          displayMessage(SUCCESS_MESSAGE, data.message);
          navigate("/lead");
        } else {
          displayMessage(ERROR_MSG_TYPE, data.message);
        }
      },
      () => {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to save lead");
      }
    );
  };

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          title="Create New Lead"
          bordered={false}
          extra={
            <Button
              type="primary"
              ghost
              icon={<RollbackOutlined />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          }
        >
          <Spin spinning={loading}>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Divider orientation="left" style={{ fontWeight: "bold" }}>
                Hotel Details
              </Divider>

              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Form.Item name="assigned_to" label="Assign BDE">
                    <Select placeholder="Select BDE">
                      {bdes.map((bde) => (
                        <Option key={bde.id} value={bde.id}>
                          {bde.name} ({bde.employee_code})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="hotel_name"
                    label="Hotel Name"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Hotel Name" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="property_type" label="Property Type">
                    <Select
                      placeholder="Select Property Type"
                      options={HOTEL_TYPE}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" style={{ fontWeight: "bold" }}>
                Contact Info
              </Divider>

              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Form.Item
                    name="contact_person"
                    label="Contact Person"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Contact Person" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="phone"
                    label="Phone"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Phone" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="email" label="Email">
                    <Input placeholder="Email" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" style={{ fontWeight: "bold" }}>
                Location Details
              </Divider>

              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Form.Item
                    name="country_id"
                    label="Country"
                    rules={[{ required: true }]}
                  >
                    <Select
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      placeholder="Select Country"
                      options={country?.map((c) => ({
                        label: c.name,
                        value: c.id,
                      }))}
                      onChange={(value) => handleOnChange("country_id", value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="state_id"
                    label="State"
                    rules={[{ required: true }]}
                  >
                    <Select
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      placeholder="Select State"
                      disabled={!countryID}
                      options={state?.map((s) => ({
                        label: s.name,
                        value: s.id,
                      }))}
                      onChange={(value) => handleOnChange("state_id", value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="city_id"
                    label="City"
                    rules={[{ required: true }]}
                  >
                    <Select
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      placeholder="Select City"
                      disabled={!stateID}
                      options={city?.map((c) => ({
                        label: c.name,
                        value: c.id,
                      }))}
                      onChange={(value) => handleOnChange("city_id", value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="area"
                    label="Area"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Area" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" style={{ fontWeight: "bold" }}>
                Extra Info
              </Divider>

              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="next_followup_date"
                    label="Next Follow-up Date"
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="enquiry_source" label="Enquiry Source">
                    <Input placeholder="Enquiry Source" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="notes" label="Notes">
                    <Input.TextArea rows={4} placeholder="Notes" />
                  </Form.Item>
                </Col>
              </Row>

              <Row justify="end">
                <Col>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Save Lead
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Card>
      </Content>
      <LayoutFooter />
    </Layout>
  );
}

export default LeadAdd;
